import { useEffect, useState } from "react";
import { PageContainer, PageContent } from "../../components/PageContainer";
import { TitleBar } from "../../components/TitleBar";
import { Button } from "../../components/Button";
import { Spinner } from "../../components/Loader";
import _ from "lodash";
import { pause } from "../../utils";
import Select from "react-select";
import {
  ILogRowData,
  IAppData,
  useAnalyticsParams,
  useUpdateAnalyticsParams,
} from "./utils";
import axios from "axios";
import { IDropdownOption } from "../../utils/customisation";
import DateRangeSelector, {
  IDateRange,
  getRangeAsLabels,
} from "./Components/DateRangeSelector";
import { differenceInDays, format } from "date-fns";
import { SingleDeviceDisplay } from "./SingleDeviceDisplay";
import API from "./api";

const Analytics = (): JSX.Element => {
  const { startRange, appId } = useAnalyticsParams();
  const updateUrl = useUpdateAnalyticsParams();

  const [isFetching, setIsFetching] = useState(false);
  const [appData, setAppData] = useState<IAppData[]>([]);

  // current data
  const [app, setApp] = useState<IAppData | null>(null);
  //const [device, setDevice] = useState<string | null>(null);
  const [dates, setDates] = useState<IDateRange | null>(null);

  // form data
  const [formApp, setFormApp] = useState<IAppData | null>(null);
  //const [formDevice, setFormDevice] = useState<string | null>(null);
  const [formDates, setFormDates] = useState<IDateRange | null>(
    startRange ? startRange : null
  );

  const [logData, setLogData] = useState<ILogRowData[]>([]);
  const [loadStatus, setLoadStatus] = useState<string | React.ReactNode>(
    startRange && appId
      ? "Press 'Fetch data' to load results"
      : "Select a date and app to fetch data"
  );

  const fetchAppIds = async () => {
    try {
      setIsFetching(true);

      const response = await axios.get(API.fetchDataUrl);
      const parsedAppData: IAppData[] = response.data.appDeviceIds.map(
        (d: any) => {
          const appId = Object.keys(d)[0];
          return { appId: Object.keys(d)[0], devices: d[appId] };
        }
      );

      setAppData(parsedAppData);
      if (app == null && appId) {
        const getApp = parsedAppData.find((d) => d.appId === appId);
        if (getApp) {
          setApp(getApp);
          setFormApp(getApp);
        }
      }
    } catch (err) {
      console.warn(`Error fetching app data: ${err}`);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchAppIds();
  }, []);

  const fetchData = async () => {
    if (!formApp || !formDates) return;
    try {
      setIsFetching(true);
      setLoadStatus("Fetching data...");

      //const deviceList = formDevice === "All Devices" ? [] : [formDevice];

      const filesResponse = await API.fetchAnalyticsData(
        formApp.appId,
        [],
        formDates,
        setLoadStatus
      );

      // Parse row data
      const parsedRows: ILogRowData[] = filesResponse || [];

      // setLoadStatus(`Parsed and loaded ${parsedRows.length} rows.`);
      setLogData(parsedRows);
      setDates(formDates);
      setApp(formApp);
      // setDevice(formDevice);

      if (parsedRows.length < 1)
        setLoadStatus("No results. Please try another time period or app.");
      else setLoadStatus(<Status />);
    } catch (err) {
      console.log(err);
      setLoadStatus(
        `There was an error retrieving analytics data. Check console for details.`
      );
    } finally {
      setIsFetching(false);
      updateUrl(formDates, formApp.appId);
    }
  };

  const appOptions: IDropdownOption[] =
    appData?.map((item) => {
      return { label: item.appId, value: item.appId };
    }) || [];

  // const deviceOptions: IDropdownOption[] = [
  //   {
  //     label: "All Devices",
  //     value: "All Devices",
  //   },
  //   ...(formApp?.devices.map((device) => {
  //     return { label: device, value: device };
  //   }) || []),
  // ];

  const numDays = dates
    ? differenceInDays(dates.endDate, dates.startDate) + 1
    : 0;

  const Controls = (): JSX.Element => {
    return (
      <div className="flex flex-col gap-4 min-w-[48rem]">
        <div className="flex gap-4 items-end">
          <div className="space-y-2">
            <div className="text-sm">Time Period</div>
            <DateRangeSelector
              name="dates"
              type=""
              onChange={(e) => {
                setFormDates(e.target.value);
              }}
              value={formDates}
            />
          </div>
          <div className="space-y-2">
            <div className="text-sm">App</div>
            <Select
              className="w-40 text-xs"
              options={appOptions}
              value={appOptions.find((o) => o.value === formApp?.appId)}
              onChange={(e) => {
                const app = appData.find((a) => a.appId === e?.value);
                setFormApp(app || null);
              }}
            />
          </div>
          {/* <div className="space-y-2">
            <div className="text-sm">Device</div>
            <Select
              className="w-48 text-xs"
              options={deviceOptions}
              value={deviceOptions.find((o) => o.value === formDevice)}
              onChange={(e) => {
                const device =
                  formApp?.devices.find((a) => a === e?.value) || "All Devices";
                setFormDevice(device);
              }}
              placeholder={!formApp ? "No app selected" : "Select..."}
              isDisabled={!formApp}
            />
          </div> */}
          <Button
            onClick={() => {
              setLogData([]);
              fetchData();
            }}
            disabled={isFetching || !formApp || !formDates}
            className="flex gap-3 text-sm"
          >
            Fetch data {isFetching && <Spinner />}
          </Button>
        </div>
      </div>
    );
  };

  const Status = () => (
    <p>
      Displaying results for <span className="font-bold">{formApp?.appId}</span>{" "}
      from{" "}
      <span className="font-bold">
        {formDates && getRangeAsLabels(formDates)}
      </span>
    </p>
  );

  return (
    <PageContainer>
      <PageContent className="space-y-4">
        <TitleBar title="Analytics" />
        {appData && <Controls />}
        <div className="border-b pb-4 text-sm">{loadStatus}</div>
        {appId && dates && (
          <SingleDeviceDisplay
            appId={appId}
            logData={logData}
            numDays={numDays}
            dateRange={dates}
          />
        )}
      </PageContent>
    </PageContainer>
  );
};

export { Analytics };
